import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Space, message, Modal, Flex, Typography, Tag, Input, Tooltip } from "antd";
import { LeftOutlined, SaveOutlined, CopyOutlined, SyncOutlined, SwapOutlined } from "@ant-design/icons";
import { useScheduleEditor } from "../context/ScheduleEditorContext";
import { scheduleService } from "../../../../api/enhancedApi";
import FlightPlannerButton from "./FlightPlannerButton";
import { ConstraintsService, SameAircraftOverlapService, StaFollowBlockTimeService, TableService } from "../services";
import BatchAirportChangeModal from "./BatchAirportChangeModal";
import BatchAirportChangeService from "../services/BatchAirportChangeService";
import { useAuth } from "../../../../hooks/useAuth";
import { confirmScheduleNameBeforeSave } from "./confirmScheduleNameBeforeSave";

const { Title } = Typography;

async function saveFlightsConfirmingRouteRevoke(scheduleId, flights, saveOptions) {
    try {
        return await scheduleService.saveScheduleFlights(scheduleId, flights, saveOptions);
    } catch (error) {
        const detail = error?.response?.data?.detail;
        const code = detail && typeof detail === "object" ? detail.code : null;
        if (error?.response?.status !== 409 || code !== "EXECUTED_ROUTES_WILL_REVOKE") {
            throw error;
        }
        const confirmed = await new Promise((resolve) => {
            Modal.confirm({
                title: "保存后会撤销已确认的执行航线",
                content: detail.message,
                okText: "确认保存",
                cancelText: "先不保存",
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
        if (!confirmed) return null;
        return scheduleService.saveScheduleFlights(scheduleId, flights, {
            ...saveOptions,
            acknowledgeExecutedRouteRevoke: true,
        });
    }
}

/**
 * 航班保存前的统一前置校验。
 * 「保存」和「另存为」共用同一套校验逻辑，避免行为漂移。
 *
 * 校验顺序：
 *   1. 必填字段（flight_number / departure_airport / arrival_airport / std / sta）
 *   2. 时间合理性（std < sta）
 *   3. 同机时间重叠硬拦（同一飞机时间轴打架则拒绝保存；用模态框展示详情）
 *   4. 空注册号达到可互相打架规模时提示无法按飞机检查（确认后仍可保存）
 *   5. 航路绑定（flight_route_id 缺失则弹确认，允许用户「忽略并保存」）
 *
 * @param {Array} flights 待保存的航班数组
 * @returns {Promise<{proceed: boolean, ignoreRouteValidation: boolean}>}
 *   - proceed=false 表示校验未通过或用户取消，调用方应直接返回；
 *   - ignoreRouteValidation=true 表示用户选择忽略航路校验，调用方需将该标志透传到后端。
 */
const validateFlightsBeforeSave = async (flights) => {
    if (!flights || flights.length === 0) {
        return { proceed: true, ignoreRouteValidation: false };
    }

    const invalidFlights = flights.filter(
        (flight) => !flight.flight_number || !flight.departure_airport || !flight.arrival_airport || !flight.std || !flight.sta,
    );
    if (invalidFlights.length > 0) {
        message.error(`存在 ${invalidFlights.length} 条不完整的航班数据，请先完善这些航班信息再保存`);
        return { proceed: false, ignoreRouteValidation: false };
    }

    const timeInvalidFlights = flights.filter((flight) => flight.std && flight.sta && new Date(flight.std) >= new Date(flight.sta));
    if (timeInvalidFlights.length > 0) {
        message.error(`存在 ${timeInvalidFlights.length} 条航班的起飞时间晚于或等于到达时间，请先修正这些航班再保存`);
        return { proceed: false, ignoreRouteValidation: false };
    }

    const orderedFlights = TableService.filterAndSortFlights(flights);
    const overlapResult = SameAircraftOverlapService.findSameAircraftTimeOverlaps(flights, {
        orderedFlights,
    });
    if (overlapResult.hasOverlap) {
        await new Promise((resolve) => {
            Modal.error({
                title: "同机时间冲突，无法保存",
                width: 640,
                content: (
                    <div style={{ whiteSpace: "pre-wrap" }}>
                        {SameAircraftOverlapService.buildSaveOverlapGateMessage(overlapResult) ||
                            "存在同机时间冲突，无法保存。请先调整时刻后再保存。"}
                    </div>
                ),
                // onOk + afterClose 都会 resolve：点确定走 onOk；点右上角关闭只走 afterClose，避免 Promise 挂起。第二次 resolve 无害。
                onOk: () => resolve(),
                afterClose: () => resolve(),
            });
        });
        return { proceed: false, ignoreRouteValidation: false };
    }

    if (SameAircraftOverlapService.shouldWarnUncheckedRegistration(overlapResult, flights.length)) {
        const uncheckedNote =
            SameAircraftOverlapService.buildUncheckedRegistrationNote(overlapResult) ||
            "部分航班未填注册号，无法按飞机完整检查。";
        const incompleteNote = SameAircraftOverlapService.buildSkippedIncompleteNote(overlapResult);
        const coverageMessage = incompleteNote
            ? `${uncheckedNote}\n\n${incompleteNote}\n\n同机时间冲突门禁无法覆盖这些航班。是否仍要保存？`
            : `${uncheckedNote}\n\n同机时间冲突门禁无法覆盖这些航班。是否仍要保存？`;
        const continueSave = await new Promise((resolve) => {
            Modal.confirm({
                title: "部分航班无法按飞机检查",
                width: 640,
                content: <div style={{ whiteSpace: "pre-wrap" }}>{coverageMessage}</div>,
                okText: "仍要保存",
                cancelText: "取消保存",
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
        if (!continueSave) {
            return { proceed: false, ignoreRouteValidation: false };
        }
    } else if ((overlapResult.skippedIncompleteCount || 0) > 0) {
        const incompleteNote = SameAircraftOverlapService.buildSkippedIncompleteNote(overlapResult);
        if (incompleteNote) {
            const continueSave = await new Promise((resolve) => {
                Modal.confirm({
                    title: "部分航班未参与同机检查",
                    width: 640,
                    content: (
                        <div style={{ whiteSpace: "pre-wrap" }}>
                            {incompleteNote}
                            {"\n\n"}
                            是否仍要保存？
                        </div>
                    ),
                    okText: "仍要保存",
                    cancelText: "取消保存",
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            });
            if (!continueSave) {
                return { proceed: false, ignoreRouteValidation: false };
            }
        }
    }

    const unboundFlights = flights.filter((flight) => !flight.flight_route_id);
    if (unboundFlights.length === 0) {
        return { proceed: true, ignoreRouteValidation: false };
    }

    console.warn(
        "发现未绑定航路的航班:",
        unboundFlights.map((f) => f.flight_number),
    );

    const shouldIgnore = await new Promise((resolve) => {
        Modal.confirm({
            title: "航路校验警告",
            width: 600,
            content: (
                <div>
                    <p style={{ marginBottom: 16, color: "#faad14" }}>有 {unboundFlights.length} 个航班缺少航路信息。</p>
                    <div style={{ maxHeight: 300, overflow: "auto", marginBottom: 16 }}>
                        <h4>缺少航路的航班：</h4>
                        <ul style={{ paddingLeft: 20 }}>
                            {unboundFlights.slice(0, 10).map((flight, idx) => (
                                <li key={idx} style={{ marginBottom: 8 }}>
                                    <strong>{flight.flight_number}</strong> ({flight.departure_airport}-{flight.arrival_airport})
                                </li>
                            ))}
                            {unboundFlights.length > 10 && <li style={{ color: "#999" }}>... 还有 {unboundFlights.length - 10} 个航班</li>}
                        </ul>
                    </div>
                    <p>是否忽略航路校验并强制保存？（保存后请手动补充航路信息）</p>
                </div>
            ),
            okText: "忽略并保存",
            cancelText: "取消保存",
            okButtonProps: { danger: true },
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });

    if (!shouldIgnore) {
        return { proceed: false, ignoreRouteValidation: false };
    }
    return { proceed: true, ignoreRouteValidation: true };
};

const confirmAircraftAvailabilityBeforeSave = async (state) => {
    const availabilityState = state.constraints?.aircraftAvailability || {};
    const { conflicts, incomplete } = ConstraintsService.getAircraftAvailabilitySaveStatus(
        state.flights || [],
        availabilityState,
    );
    if (!conflicts.length && !incomplete) {
        return true;
    }

    return new Promise((resolve) => {
        Modal.confirm({
            title: "飞机可用性提示",
            width: 680,
            content: (
                <div>
                    {conflicts.length > 0 && (
                        <>
                            <p style={{ color: "#cf1322" }}>
                                当前有 {conflicts.length} 个航班与飞机不可用时段重叠。草稿可以继续保存，但正式发布会被拒绝。
                            </p>
                            <ul style={{ maxHeight: 240, overflow: "auto", paddingLeft: 20 }}>
                                {conflicts.slice(0, 20).map((conflict, index) => (
                                    <li key={`${conflict.flightId}-${conflict.windowId || index}`}>
                                        {(conflict.messages || [])[0] || `航班 ${conflict.flightId} 与不可用窗口重叠`}
                                    </li>
                                ))}
                                {conflicts.length > 20 && <li>另有 {conflicts.length - 20} 个冲突未展开</li>}
                            </ul>
                        </>
                    )}
                    {incomplete && (
                        <p style={{ color: "#d46b08", marginBottom: 0 }}>
                            本次飞机可用性校验未完成或数据可能已过期：
                            {availabilityState.error?.message || "数据仍在加载，请稍后刷新基础数据复核"}。
                        </p>
                    )}
                </div>
            ),
            okText: "确认保存草稿",
            cancelText: "取消保存",
            okButtonProps: { danger: conflicts.length > 0 },
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
};

const ScheduleEditorHeader = ({ isNew = false, mode = "page", onClose, onSaveSuccess }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { state, setIsSaving, updateSchedule, updateParameters, setFlights, batchUpdateFlights, resetDirtyStatus, refreshMasterDataAndUpdateFlights, appendBatchAirportChangeLog } = useScheduleEditor();
    const { schedule, flights, parameters, isDirty } = state;

    const [batchChangeModalOpen, setBatchChangeModalOpen] = useState(false);
    const [openLeakPrompt, setOpenLeakPrompt] = useState({ visible: false, leaks: [] });
    const [saveLeakState, setSaveLeakState] = useState(null);
    const scannedScheduleKeyRef = useRef(null);
    const saveLeakStateRef = useRef(null);

    useEffect(() => {
        saveLeakStateRef.current = saveLeakState;
    }, [saveLeakState]);

    useEffect(() => {
        const key = schedule?.id != null ? String(schedule.id) : "new";
        if (key === "new" || isNew) {
            scannedScheduleKeyRef.current = key;
            setOpenLeakPrompt({ visible: false, leaks: [] });
            return;
        }
        if (!Array.isArray(flights) || flights.length === 0) return;
        const leaks = StaFollowBlockTimeService.findLeaks(flights);
        if (scannedScheduleKeyRef.current !== key) {
            scannedScheduleKeyRef.current = key;
            setOpenLeakPrompt({ visible: leaks.length > 0, leaks });
            return;
        }
        setOpenLeakPrompt((prev) => {
            if (!prev.visible) return prev;
            return { visible: leaks.length > 0, leaks };
        });
    }, [schedule?.id, flights, isNew]);

    const confirmUndeclaredStaLeaks = useCallback((currentFlights) => {
        const leaks = StaFollowBlockTimeService.findLeaks(currentFlights);
        if (leaks.length === 0) {
            return Promise.resolve({
                proceed: true,
                flights: StaFollowBlockTimeService.prepareFlightsForSave(currentFlights),
            });
        }
        return new Promise((resolve) => {
            const next = { remaining: leaks, workingFlights: currentFlights, resolve };
            saveLeakStateRef.current = next;
            setSaveLeakState(next);
        });
    }, []);

    const closeSaveLeakGate = useCallback((proceed) => {
        const prev = saveLeakStateRef.current;
        if (!prev) return;
        prev.resolve({
            proceed,
            flights: proceed ? StaFollowBlockTimeService.prepareFlightsForSave(prev.workingFlights) : prev.workingFlights,
        });
        setSaveLeakState(null);
    }, []);

    const applySaveLeakChoice = useCallback(
        (ids, mode) => {
            const prev = saveLeakStateRef.current;
            if (!prev) return;
            const nextFlights =
                mode === "align"
                    ? StaFollowBlockTimeService.applyAlignedSta(prev.workingFlights, ids)
                    : StaFollowBlockTimeService.applyKeepManualSta(prev.workingFlights, ids);
            batchUpdateFlights(nextFlights);
            const remaining = StaFollowBlockTimeService.findLeaks(nextFlights);
            if (remaining.length === 0) {
                prev.resolve({
                    proceed: true,
                    flights: StaFollowBlockTimeService.prepareFlightsForSave(nextFlights),
                });
                setSaveLeakState(null);
                return;
            }
            setSaveLeakState({ ...prev, workingFlights: nextFlights, remaining });
        },
        [batchUpdateFlights],
    );

    const handleApplyOpenLeakPrompt = useCallback(() => {
        const ids = openLeakPrompt.leaks.map((leak) => leak.id);
        const nextFlights = StaFollowBlockTimeService.applyAlignedSta(flights, ids);
        batchUpdateFlights(nextFlights);
        setOpenLeakPrompt({ visible: false, leaks: [] });
        message.success(`已按轮档更新 ${ids.length} 班到达，起飞没动。尚未保存。`);
    }, [openLeakPrompt.leaks, flights, batchUpdateFlights]);

    const handleDismissOpenLeakPrompt = useCallback(() => {
        setOpenLeakPrompt((prev) => ({ ...prev, visible: false }));
    }, []);

    const isDraft = schedule.status === "DRAFT";

    const handleBatchChangeClose = useCallback(
        (result) => {
            setBatchChangeModalOpen(false);
            if (!result) return;

            const { preview, fromAirport, toAirport, sides, skipUnmatchedRoutes } = result;
            const updatedFlights = BatchAirportChangeService.applyPreviewToFlights(flights, preview.legs);
            setFlights(updatedFlights);

            const log = BatchAirportChangeService.buildChangeLog({
                fromAirport,
                toAirport,
                sides,
                skipUnmatchedRoutes,
                summary: preview.summary,
                operator: user?.username || null,
            });
            appendBatchAirportChangeLog(log);

            const warningCount =
                preview.summary.ground_time_warning_count +
                preview.summary.curfew_warning_count +
                preview.summary.same_airport_warning_count;

            if (warningCount > 0) {
                message.warning(
                    `已写回 ${preview.summary.matched_flight_count} 个航班（含 ${warningCount} 个警告），请查看后再决定是否保存`,
                    5,
                );
            } else {
                message.success(
                    `已写回 ${preview.summary.matched_flight_count} 个航班，尚未保存`,
                );
            }
        },
        [flights, setFlights, appendBatchAirportChangeLog],
    );

    // 返回上一页
    const handleBack = () => {
        // 如果有未保存更改，先确认
        if (isDirty) {
            Modal.confirm({
                title: "确认离开",
                content: "您有未保存的更改，确定要离开吗？",
                okText: "确认",
                cancelText: "取消",
                onOk: () => {
                    if (mode === "modal" && onClose) {
                        onClose();
                    } else {
                        navigate("/flightSchedule/drafts");
                    }
                },
            });
            return;
        }

        // 没有未保存更改
        if (mode === "modal" && onClose) {
            onClose();
            return;
        }

        // 如果存在全局导航函数，使用它
        if (window.currentScheduleEditor && window.currentScheduleEditor.customNavigate) {
            window.currentScheduleEditor.customNavigate("/flightSchedule/drafts");
        } else {
            // 直接导航
            navigate("/flightSchedule/drafts");
        }
    };

    // 另存为新计划
    const handleSaveAs = async () => {
        // 先进行与保存相同的验证
        const basicInfoForm = window.antdForms && window.antdForms["scheduleBasicInfoForm"];
        if (basicInfoForm) {
            try {
                await basicInfoForm.validateFields();
            } catch (validationError) {
                message.error("请检查基本信息表单中的错误");
                return;
            }
        }
        if (!schedule.name || !schedule.start_date || !schedule.end_date || !schedule.aircraft_id) {
            message.error("请确保计划名称、日期范围和执行飞机都已填写");
            return;
        }

        // 弹出对话框让用户输入新计划的名称
        let newScheduleName = `${schedule.name} - 副本`;
        const shouldProceed = await new Promise((resolve) => {
            Modal.confirm({
                title: "另存为新计划",
                content: (
                    <Input
                        addonBefore="新计划名称"
                        defaultValue={newScheduleName}
                        onChange={(e) => {
                            newScheduleName = e.target.value;
                        }}
                    />
                ),
                okText: "确认创建",
                cancelText: "取消",
                onOk: () => {
                    if (!newScheduleName || !newScheduleName.trim()) {
                        message.error("新计划名称不能为空");
                        resolve(false);
                    } else {
                        resolve(true);
                    }
                },
                onCancel: () => resolve(false),
            });
        });

        if (!shouldProceed) {
            return;
        }

        const nameConfirmation = await confirmScheduleNameBeforeSave(newScheduleName);
        if (!nameConfirmation.proceed) {
            return;
        }
        newScheduleName = nameConfirmation.name;

        // 航班数据前置校验（与「保存」按钮保持一致的行为）
        // 必须在创建新计划之前完成，否则用户取消时会留下脏数据
        const validation = await validateFlightsBeforeSave(flights);
        if (!validation.proceed) {
            return;
        }
        const ignoreRouteValidation = validation.ignoreRouteValidation;
        if (!(await confirmAircraftAvailabilityBeforeSave(state))) {
            return;
        }

        // 追踪已创建的资源，便于失败时回滚清理，避免脏数据
        let createdScheduleId = null;

        try {
            setIsSaving(true);

            // 1. 创建新的计划基础信息
            const scheduleData = {
                name: newScheduleName,
                start_date: schedule.start_date,
                end_date: schedule.end_date,
                status: "DRAFT", // 始终为草稿
                aircraft_id: schedule.aircraft_id,
                tags: schedule.tags || [],
            };

            const createResponse = await scheduleService.createSchedule(scheduleData);
            const newSchedule = createResponse?.data;
            if (!newSchedule || !newSchedule.id) {
                throw new Error("另存为失败：创建新计划时未返回ID");
            }
            const newScheduleId = newSchedule.id;
            createdScheduleId = newScheduleId;
            message.success(`新计划 '${newScheduleName}' 创建成功`);

            // 2. 为新计划创建参数
            if (parameters) {
                const paramContent = {
                    schedule_id: newScheduleId,
                    flight_number_prefix: parameters.flight_number_prefix || "",
                    min_ground_time: parameters.min_ground_time || 60,
                    transportation_demands: parameters.transportation_demands || [],
                    available_segments: parameters.available_segments || [],
                    maintenance_requirements: parameters.maintenance_requirements || {},
                    curfew_constraints: parameters.curfew_constraints || [],
                    flight_compare_result: parameters.flight_compare_result || null,
                };
                const parametersData = {
                    parameter_name: `计划 ${newScheduleId} 参数配置`,
                    parameter_json: paramContent,
                };
                const paramResponse = await scheduleService.createScheduleParameter(parametersData);
                const newParameter = paramResponse?.data;
                if (newParameter && newParameter.id) {
                    await scheduleService.updateSchedule(newScheduleId, {
                        parameter_id: newParameter.id,
                    });
                    message.success("新计划的参数已成功复制");
                }
            }

            // 3. 为新计划复制航班数据
            if (flights && flights.length > 0) {
                const flightsWithNewScheduleId = StaFollowBlockTimeService.prepareFlightsForSave(flights).map((flight, index) => ({
                    ...flight,
                    id: undefined, // 清除旧的航班ID，让后端创建新的
                    schedule_id: newScheduleId,
                    display_order: index + 1,
                }));

                const saveOptions = ignoreRouteValidation ? { ignore_route_validation: true } : {};
                if (state.batchAirportChangeLogs?.length > 0) {
                    saveOptions.scheduleAdditionalInfoMerge = {
                        batch_airport_change_logs: state.batchAirportChangeLogs,
                    };
                }
                const flightSaveResponse = await saveFlightsConfirmingRouteRevoke(newScheduleId, flightsWithNewScheduleId, saveOptions);
                if (!flightSaveResponse) return;
                const flightSaveResult = flightSaveResponse?.data;
                if (flightSaveResult && flightSaveResult.success) {
                    message.success(`已成功复制 ${flightSaveResult.count || 0} 条航班数据`);
                } else {
                    message.warning(`复制航班数据时发生错误: ${flightSaveResult ? flightSaveResult.message : "未知原因"}`);
                }
            }

            // 走到这里表示所有步骤都成功，无需回滚
            createdScheduleId = null;

            // 4. 操作成功后，跳转到新创建的计划的编辑页面
            message.info("即将跳转到新计划的编辑页面...");
            if (onSaveSuccess) onSaveSuccess();
            navigate(`/flightSchedule/editor/${newScheduleId}`, { replace: true });
        } catch (error) {
            console.error("另存为操作失败:", error);

            if (error.response && error.response.data) {
                const errorData = error.response.data;

                if (errorData.detail && typeof errorData.detail === "object" && errorData.detail.failed_flights) {
                    const { message: detailMsg, failed_flights } = errorData.detail;
                    Modal.error({
                        title: "另存为失败：航路校验未通过",
                        width: 600,
                        content: (
                            <div>
                                <p style={{ marginBottom: 16, color: "#ff4d4f" }}>{detailMsg}</p>
                                <div style={{ maxHeight: 300, overflow: "auto", marginBottom: 16, background: "#f5f5f5", padding: "12px", borderRadius: "4px" }}>
                                    <h4 style={{ marginTop: 0 }}>问题航班清单：</h4>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {failed_flights.map((flight, idx) => (
                                            <li key={idx} style={{ marginBottom: 4 }}>
                                                <strong>{flight.flight_number}</strong>
                                                <span style={{ margin: "0 8px" }}>{flight.leg}</span>
                                                <Tag color="red">{flight.reason}</Tag>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <p>请返回表格检查上述航班的航路选择，确保选择了有效的活跃航路。</p>
                            </div>
                        ),
                    });
                } else {
                    const detail = errorData.detail;
                    const errorMsg = typeof detail === "object" ? JSON.stringify(detail) : detail || errorData.message || error.message || "未知错误";
                    message.error(`另存为失败: ${errorMsg}`);
                }
            } else {
                message.error(`另存为失败: ${error.message || "未知错误"}`);
            }
        } finally {
            // 回滚：若已创建新计划但后续步骤失败，删除空壳计划避免脏数据
            // 注意：清理失败不应阻塞 UI 恢复，仅记录日志并提示用户
            if (createdScheduleId) {
                try {
                    await scheduleService.deleteSchedule(createdScheduleId);
                    message.info(`已清理未完成的新计划草稿 (ID=${createdScheduleId})`);
                } catch (cleanupError) {
                    console.error("清理未完成的新计划草稿失败:", cleanupError);
                    message.warning(`新计划草稿 (ID=${createdScheduleId}) 未能自动清理，请手动删除`);
                }
            }
            setIsSaving(false);
        }
    };

    // 保存计划
    const handleSave = async () => {
        try {
            // 检查计划状态，只允许保存草稿状态的计划
            if (!isNew && schedule.status !== "DRAFT") {
                message.error("只能保存草稿状态的计划");
                return;
            }

            // 首先验证基本信息表单
            const basicInfoForm = window.antdForms && window.antdForms["scheduleBasicInfoForm"];

            if (basicInfoForm) {
                try {
                    await basicInfoForm.validateFields();
                } catch (validationError) {
                    console.error("基本信息表单验证失败:", validationError);
                    message.error("请检查基本信息表单中的错误");
                    return;
                }
            } else {
                console.warn("未找到基本信息表单实例，将跳过表单验证");
            }

            // 再次从context状态确认关键字段
            if (!schedule.name) {
                message.error("请输入计划名称");
                return;
            }

            if (!schedule.start_date || !schedule.end_date) {
                message.error("请选择计划日期范围");
                return;
            }

            if (!schedule.aircraft_id) {
                message.error("请选择执行飞机");
                return;
            }

            const nameConfirmation = await confirmScheduleNameBeforeSave(schedule.name);
            if (!nameConfirmation.proceed) {
                return;
            }
            const scheduleNameForSave = nameConfirmation.name;
            if (nameConfirmation.cleaned) {
                basicInfoForm?.setFieldsValue({ name: scheduleNameForSave });
                updateSchedule({ name: scheduleNameForSave });
                message.info("已清理计划名称中的不可见字符，将使用清理后的名称保存");
            }

            // 检查是否包含 PATTERN 标签
            if (schedule.tags && Array.isArray(schedule.tags) && schedule.tags.includes("PATTERN")) {
                // 弹出确认对话框
                const shouldContinue = await new Promise((resolve) => {
                    Modal.confirm({
                        title: "标签警告",
                        content: "当前计划包含 PATTERN 标签，确定要保存吗？",
                        okText: "确认保存",
                        cancelText: "取消",
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false),
                    });
                });

                // 如果用户取消，则终止保存
                if (!shouldContinue) {
                    return;
                }
            }

            // 点「保存」时：未声明的漏改不能进库。点「另存为」不加这道检查。
            const leakResult = await confirmUndeclaredStaLeaks(flights);
            if (!leakResult.proceed) {
                return;
            }
            const flightsForSave = leakResult.flights;

            // 航班数据前置校验（必填字段 / 时间合理性 / 航路绑定）
            // 校验未通过或用户取消时直接返回；未绑定航路且用户选择忽略时透传 ignore_route_validation
            const validation = await validateFlightsBeforeSave(flightsForSave);
            if (!validation.proceed) {
                return;
            }
            const ignoreRouteValidation = validation.ignoreRouteValidation;
            if (!(await confirmAircraftAvailabilityBeforeSave(state))) {
                return;
            }

            // 设置保存中状态
            setIsSaving(true);

            // 准备请求数据 - 确保状态始终为DRAFT
            const scheduleData = {
                name: scheduleNameForSave,
                start_date: schedule.start_date,
                end_date: schedule.end_date,
                status: "DRAFT", // 强制设置为草稿状态
                aircraft_id: schedule.aircraft_id,
                tags: schedule.tags || [],
            };

            let result;
            let scheduleId = schedule.id;

            // 创建或更新计划
            if (isNew) {
                const response = await scheduleService.createSchedule(scheduleData); // 获取完整响应
                // 从 response.data 中提取数据
                const createdSchedule = response?.data;
                if (!createdSchedule || !createdSchedule.id) {
                    throw new Error("创建计划失败: 未返回计划ID");
                }
                scheduleId = createdSchedule.id;

                // 更新上下文中的计划ID和状态
                updateSchedule({
                    ...createdSchedule,
                    id: scheduleId,
                    status: "DRAFT",
                });

                if (onSaveSuccess) onSaveSuccess();

                message.success("计划创建成功");
            } else {
                result = await scheduleService.updateSchedule(schedule.id, scheduleData);

                // 更新上下文中的状态
                updateSchedule({
                    status: "DRAFT", // 确保状态为草稿
                });

                message.success("计划基本信息保存成功");
                if (onSaveSuccess) onSaveSuccess();
            }

            // 保存计划参数
            if (scheduleId && parameters) {
                try {
                    // 准备参数数据的内容
                    const paramContent = {
                        schedule_id: scheduleId,
                        flight_number_prefix: parameters.flight_number_prefix || "",
                        min_ground_time: parameters.min_ground_time || 60,
                        transportation_demands: parameters.transportation_demands || [],
                        available_segments: parameters.available_segments || [],
                        maintenance_requirements: parameters.maintenance_requirements || {},
                        curfew_constraints: parameters.curfew_constraints || [],
                        // 添加对比结果
                        flight_compare_result: parameters.flight_compare_result || null,
                    };

                    // 按照后端API要求的格式包装数据
                    const parametersData = {
                        parameter_name: `计划 ${scheduleId} 参数配置`,
                        parameter_json: paramContent,
                    };

                    let paramResultResponse;

                    if (parameters.id) {
                        paramResultResponse = await scheduleService.updateScheduleParameter(parameters.id, parametersData);
                    } else {
                        paramResultResponse = await scheduleService.createScheduleParameter(parametersData); // 获取完整响应
                        const createdParameter = paramResultResponse?.data; // 从 response.data 提取

                        // 获取新创建的参数ID并关联到计划
                        if (createdParameter && createdParameter.id) {
                            // 更新计划中的parameter_id字段（返回值未被使用，暂不修改响应处理）
                            await scheduleService.updateSchedule(scheduleId, {
                                parameter_id: createdParameter.id,
                            });

                            // 更新上下文中的参数ID
                            updateParameters({
                                ...parameters,
                                id: createdParameter.id,
                            });
                        }
                    }
                    message.success("计划参数保存成功");
                } catch (paramError) {
                    console.error("保存计划参数失败:", paramError);
                    message.error("计划参数保存失败: " + (paramError.message || "未知错误"));
                    throw paramError; // 抛出错误，中断保存流程
                }
            }

            // 保存航班数据
            if (scheduleId && flightsForSave && flightsForSave.length > 0) {
                try {
                    // 为每个航班添加计划ID并确保display_order正确
                    // 首先按照当前的display_order排序
                    const sortedFlights = [...flightsForSave].sort((a, b) => {
                        if (a.display_order !== undefined && b.display_order !== undefined) {
                            return a.display_order - b.display_order;
                        }
                        // 如果没有display_order，则按照flight_date和flight_number排序
                        if (a.flight_date !== b.flight_date) {
                            return new Date(a.flight_date) - new Date(b.flight_date);
                        }
                        return a.flight_number.localeCompare(b.flight_number);
                    });

                    // 为每个航班分配规范的display_order值并添加schedule_id
                    const flightsWithScheduleId = sortedFlights.map((flight, index) => ({
                        ...flight,
                        schedule_id: scheduleId,
                        display_order: index + 1, // 确保display_order是连续的整数
                    }));

                    // 根据之前的用户选择，决定是否忽略航路验证
                    const saveOptions = ignoreRouteValidation ? { ignore_route_validation: true } : {};

                    if (state.batchAirportChangeLogs?.length > 0) {
                        saveOptions.scheduleAdditionalInfoMerge = {
                            batch_airport_change_logs: state.batchAirportChangeLogs,
                        };
                    }

                    const flightSaveResponse = await saveFlightsConfirmingRouteRevoke(scheduleId, flightsWithScheduleId, saveOptions);
                    if (!flightSaveResponse) return;
                    const flightSaveResult = flightSaveResponse?.data;

                    // 后端已改为全有或全无，只需处理成功情况
                    if (flightSaveResult && flightSaveResult.success) {
                        setFlights(flightsWithScheduleId);
                        message.success(`航班数据保存成功: ${flightSaveResult.count || 0}条记录`);
                    }

                    resetDirtyStatus();

                    // 如果是新建计划，保存完成后重定向到编辑页面
                    if (isNew && scheduleId) {
                        navigate(`/flightSchedule/editor/${scheduleId}`, { replace: true });
                    }
                } catch (flightError) {
                    console.error("保存航班数据失败:", flightError);
                    // 处理错误
                    let errorMsg = "航班数据保存失败: ";

                    if (flightError.response && flightError.response.data) {
                        const errorData = flightError.response.data;

                        // 专门处理后端返回的航路校验详细错误
                        // 后端返回格式: detail: { message: "...", failed_flights: [...], failed_legs: [...] }
                        if (errorData.detail && typeof errorData.detail === "object" && errorData.detail.failed_flights) {
                            const { message: detailMsg, failed_flights } = errorData.detail;

                            Modal.error({
                                title: "保存失败：航路校验未通过",
                                width: 600,
                                content: (
                                    <div>
                                        <p style={{ marginBottom: 16, color: "#ff4d4f" }}>{detailMsg}</p>
                                        <div style={{ maxHeight: 300, overflow: "auto", marginBottom: 16, background: "#f5f5f5", padding: "12px", borderRadius: "4px" }}>
                                            <h4 style={{ marginTop: 0 }}>问题航班清单：</h4>
                                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                                {failed_flights.map((flight, idx) => (
                                                    <li key={idx} style={{ marginBottom: 4 }}>
                                                        <strong>{flight.flight_number}</strong>
                                                        <span style={{ margin: "0 8px" }}>{flight.leg}</span>
                                                        <Tag color="red">{flight.reason}</Tag>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <p>请返回表格检查上述航班的航路选择，确保选择了有效的活跃航路。</p>
                                    </div>
                                ),
                            });

                            throw flightError; // 中断流程
                        }

                        if (errorData.errors && Array.isArray(errorData.errors)) {
                            // 处理每个航班的错误
                            errorMsg += `发现 ${errorData.errors.length} 个验证错误`;
                            console.error("航班验证错误详情:", errorData.errors);
                        } else {
                            // 处理 detail 为字符串的情况，防止 [object Object]
                            const detail = errorData.detail;
                            if (typeof detail === "object") {
                                // 如果 detail 是对象但不是上述特定格式，尝试转为字符串
                                errorMsg += JSON.stringify(detail);
                            } else {
                                errorMsg += detail || errorData.message || flightError.message || "未知错误";
                            }
                        }
                    } else {
                        errorMsg += flightError.message || "未知错误";
                    }

                    message.error(errorMsg);
                    throw flightError; // 抛出错误，中断保存流程
                }
            }
        } catch (error) {
            // 处理创建/更新计划本身的错误
            console.error("保存计划失败:", error);
            setIsSaving(false);
        } finally {
            // 确保无论成功或失败，最终都关闭加载状态
            setIsSaving(false);
        }
    };

    return (
        <div style={{ background: "#fff", padding: "16px 24px", borderBottom: "1px solid #f0f0f0" }}>
            <Flex justify="space-between" align="center">
                <Flex align="center" gap={12}>
                    <Button icon={<LeftOutlined />} onClick={handleBack} type="text" />
                    <Flex align="center" gap={8}>
                        {mode === "modal" ? (
                            <Title level={4} style={{ margin: 0 }}>
                                {schedule.name || (isNew ? "创建航班计划" : "编辑航班计划")}
                            </Title>
                        ) : (
                            <>
                                <Title level={4} style={{ margin: 0 }}>
                                    {isNew ? "创建航班计划" : "编辑航班计划"}
                                    <span style={{ color: "#999", fontSize: "12px", marginLeft: "8px" }}>v1.4.9</span>
                                </Title>
                                {schedule.name && <Typography.Text style={{ fontSize: "16px", marginLeft: "12px" }}>{schedule.name}</Typography.Text>}
                            </>
                        )}
                        <Tag color="orange">草稿</Tag>
                    </Flex>
                </Flex>

                <Space>
                    {isDirty && (
                        <Typography.Text type="warning" style={{ fontSize: "14px" }}>
                            <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#faad14", marginRight: "6px" }}></span>
                            有未保存的更改
                        </Typography.Text>
                    )}
                    <FlightPlannerButton />
                    {isDraft && !isNew && (
                        <Tooltip title="把某个经停机场一次性换成另一个，系统重绑航路并更新轮档">
                            <Button
                                icon={<SwapOutlined />}
                                onClick={() => setBatchChangeModalOpen(true)}
                                disabled={!flights || flights.length === 0}
                            >
                                批量改机场
                            </Button>
                        </Tooltip>
                    )}
                    <Button icon={<SyncOutlined />} onClick={refreshMasterDataAndUpdateFlights}>
                        <Tooltip title="按最新的基础数据（航路、机场、最小过站时间等）更新每个航班">刷新基础数据</Tooltip>
                    </Button>
                    <Button key="saveAs" icon={<CopyOutlined />} loading={state.isSaving} onClick={handleSaveAs} disabled={isNew}>
                        另存为
                    </Button>
                    <Button key="save" type="primary" icon={<SaveOutlined />} loading={state.isSaving} onClick={handleSave}>
                        保存
                    </Button>
                </Space>
            </Flex>

            {openLeakPrompt.visible && openLeakPrompt.leaks.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                    message={`有 ${openLeakPrompt.leaks.length} 班到达不等于起飞加当前轮档`}
                    description={
                        <div>
                            <p style={{ marginBottom: 8 }}>起飞保持不动。点主按钮一次按轮档更新这些班的到达；点「先看着」则先不改，表格仍标出差多少分钟。</p>
                            <ul style={{ margin: "0 0 8px 0", paddingLeft: 20, maxHeight: 160, overflow: "auto" }}>
                                {openLeakPrompt.leaks.map((leak) => (
                                    <li key={leak.id}>
                                        {leak.flight_number} {leak.departure_airport}-{leak.arrival_airport}：现在 {StaFollowBlockTimeService.formatDateClock(leak.currentSta)}（UTC），按轮档该是 {StaFollowBlockTimeService.formatDateClock(leak.expectedSta)}（UTC），差 {leak.deltaMinutes} 分
                                        {leak.expiredManual ? "（手填已过期）" : ""}
                                    </li>
                                ))}
                            </ul>
                            <Space>
                                <Button type="primary" size="small" onClick={handleApplyOpenLeakPrompt}>
                                    按轮档更新到达
                                </Button>
                                <Button size="small" onClick={handleDismissOpenLeakPrompt}>
                                    先看着
                                </Button>
                            </Space>
                        </div>
                    }
                />
            )}

            <Modal
                title="保存前：这些班到达和轮档对不上"
                open={Boolean(saveLeakState)}
                width={720}
                maskClosable={false}
                onCancel={() => closeSaveLeakGate(false)}
                footer={
                    saveLeakState ? (
                        <Space>
                            <Button onClick={() => closeSaveLeakGate(false)}>取消保存</Button>
                            <Button
                                type="primary"
                                onClick={() => applySaveLeakChoice(saveLeakState.remaining.map((leak) => leak.id), "align")}
                            >
                                全部改成轮档到达
                            </Button>
                        </Space>
                    ) : null
                }
            >
                {saveLeakState && (
                    <div>
                        <p>未声明的漏改不能保存。请逐班选择「改成轮档到达」或「保留手填到达」。点「另存为」不会走这道检查。</p>
                        <ul style={{ paddingLeft: 20, maxHeight: 360, overflow: "auto" }}>
                            {saveLeakState.remaining.map((leak) => (
                                <li key={leak.id} style={{ marginBottom: 10 }}>
                                    <div>
                                        {leak.flight_number} {leak.departure_airport}-{leak.arrival_airport}：现在 {StaFollowBlockTimeService.formatDateClock(leak.currentSta)}（UTC），按轮档该是 {StaFollowBlockTimeService.formatDateClock(leak.expectedSta)}（UTC），差 {leak.deltaMinutes} 分
                                    </div>
                                    <Space style={{ marginTop: 4 }}>
                                        <Button size="small" type="primary" onClick={() => applySaveLeakChoice([leak.id], "align")}>
                                            改成轮档到达
                                        </Button>
                                        <Button size="small" onClick={() => applySaveLeakChoice([leak.id], "manual")}>
                                            保留手填到达
                                        </Button>
                                    </Space>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </Modal>

            <BatchAirportChangeModal
                open={batchChangeModalOpen}
                onClose={handleBatchChangeClose}
            />
        </div>
    );
};

export default ScheduleEditorHeader;
